import { useEffect, useRef } from "react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { useEditorUpload } from "@/payroll/editor/editor-documents/use-editor-upload";
import type { PayrollEditorQueries } from "@/payroll/editor/query";
import { editorError } from "@/payroll/editor/state";
import type { Workspace } from "@/payroll/editor/types";
import { EditorComposer } from "./editor-composer";
import { EditorTranscript } from "./editor-transcript";
import { useComposer } from "./use-composer";
import { useOutgoing } from "./use-outgoing";

type ConversationQueries = Pick<
  PayrollEditorQueries,
  | "messages"
  | "documents"
  | "send"
  | "upload"
  | "remove"
  | "refresh"
  | "open"
  | "cancel"
>;

export const EditorConversation = ({
  editor,
  workspace,
  businessId,
  workspaceId,
  storageKey,
}: {
  editor: ConversationQueries;
  workspace: Workspace;
  businessId: string;
  workspaceId: string;
  storageKey: string;
}) => {
  const composer = useComposer();
  const upload = useEditorUpload(`${storageKey}:upload`, editor.upload);
  const active = editor.messages.data?.isActive ?? false;
  const filesNotReady =
    editor.documents.data?.some((file) => file.status !== "ready") ?? false;
  // Pressing send sends. The only thing that holds a message back is a request
  // already running, because the server refuses a second one — that is what the
  // queue is for, and it drains the moment the assistant has fully returned.
  //
  // Loading states used to gate it too: a documents query still in flight, a
  // file being removed, a stale messages error. Those are states of the page,
  // not of the message, and gating on them meant the first thing typed on a
  // freshly opened editor was silently queued instead of sent.
  const canSend = workspace.editable && !filesNotReady && !upload.isUploading;
  const outgoing = useOutgoing(
    `${storageKey}:outgoing`,
    editor.send,
    editor.messages.data,
    businessId,
    workspaceId,
    canSend,
  );
  const busy = active || !!outgoing.sending;
  // A failed Stop belongs to the request it could not stop. Held by the
  // mutation alone it outlived that request, so an old failure could appear
  // beside a new one; and it only matters while there is still something to
  // stop.
  const stopFailed =
    busy &&
    !!editor.cancel.error &&
    editor.cancel.variables === editor.messages.data?.latest_request?.id;
  const submit = () => {
    const message = composer.text.trim();
    if (!message) return;
    composer.setText("");
    outgoing.add(message, !busy);
  };
  /**
   * Stops the running request so this one goes next.
   *
   * It moves to the head and keeps its id, so the drain picks it up as soon as
   * the worker stops. Taking it out and adding it back would send it to the
   * back of the queue with a new id, which is neither what the button says nor
   * idempotent on a retry.
   */
  /**
   * An offered answer is an answer. Clicking one used to type it into the
   * composer and wait for the reader to press send again, which is a second
   * decision about something they had already decided. Any draft they had
   * started is left alone: this sends the choice, not the box.
   */
  const reply = (choice: string) => {
    if (!canSend) return;
    outgoing.add(choice, !busy);
  };
  /**
   * Sends the same words again after a request ended badly.
   *
   * A new message rather than the same one: that request is over, and the
   * server starts a turn for a message it has not seen before.
   */
  const resend = (request: string) => {
    if (!canSend || !request.trim()) return;
    outgoing.add(request, !busy);
  };
  const sendNow = (id: string) => {
    outgoing.promote(id);
    if (active) editor.cancel.mutate(editor.messages.data?.latest_request?.id);
  };

  // Send is never dead without saying why.
  const blockedReason = filesNotReady
    ? "Waiting for files"
    : upload.isUploading
      ? "Adding a file"
      : "";

  /**
   * Typing anywhere in this pane types into the composer.
   *
   * The conversation is most of the pane and none of it takes text, so a
   * reader who had clicked a message, or had just landed here, pressed a key
   * and nothing happened. The character is carried over by hand rather than
   * left to the browser: focus moves during the keydown, and where the
   * character lands after that is not the same in every browser.
   *
   * On the document rather than on the pane, because a key event only reaches
   * the node that has focus and almost nothing in here can take focus. The
   * pane itself can: it carries `tabIndex={-1}`, so a click anywhere inside it
   * lands focus on the pane, and focus inside the pane is the whole test.
   *
   * It used to count focus being nowhere as well, which is where focus sits
   * after a plain scroll, so a reader scrolling the comparison on the other
   * side of the screen typed into the composer without touching it.
   *
   * Only a bare printable key, and never the space bar: space scrolls, and a
   * reader paging through the transcript with it meant to scroll. Shortcuts
   * keep their meaning, and a key pressed on a control, or in any field, is
   * that control's.
   */
  const pane = useRef<HTMLElement>(null);
  const { append } = composer;
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.length !== 1 || event.key === " ") return;
      const active = document.activeElement;
      if (!active || !pane.current?.contains(active)) return;
      if (
        event.target instanceof Element &&
        event.target.closest(
          "input, textarea, select, button, a, [contenteditable='true'], [role='button'], [role='menuitem'], [role='tab']",
        )
      ) {
        return;
      }
      event.preventDefault();
      append(event.key);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [append]);

  return (
    <section
      ref={pane}
      // Focusable, but not in the tab order. A click on the transcript has
      // nothing of its own to focus, so without this the browser left focus
      // where it was and the pane never heard the keys meant for it.
      tabIndex={-1}
      className="flex min-h-0 min-w-0 flex-col outline-none lg:border-r lg:border-stroke-tertiary"
      aria-label="Payroll change conversation"
    >
      <EditorTranscript
        page={editor.messages.data}
        loading={editor.messages.isPending && !editor.messages.data}
        working={!!workspace.active_request}
        isSending={!!outgoing.sending}
        onReply={reply}
        onWriteOwn={composer.focus}
        onResend={resend}
        canResend={canSend}
        posted={outgoing.posted}
        onRetry={outgoing.retry}
        onDiscard={outgoing.remove}
        queueCount={outgoing.queued.length}
      />
      {/* A failed Stop only matters while there is something to stop. The
          request it could not stop finishes a moment later more often than
          not, and the sentence used to stay on screen afterwards, telling the
          owner to try again on a request that had already ended. Derived from
          `busy`, so it goes when the reason for it goes. */}
      {!!(editor.messages.error || stopFailed) && (
        <Stack
          gap="none"
          align="stretch"
          direction="row"
          role="alert"
          className="flex flex-wrap items-center gap-3 px-5 py-2 text-sm"
        >
          <span>
            {stopFailed
              ? "Could not stop the request. Try again when the current request finishes."
              : editorError(editor.messages.error)}
          </span>
          {/* Refresh clears what it is offered for. Invalidating the queries
              left the mutation holding its old error, so the line it was meant
              to clear was still there afterwards. */}
          <Button
            size="xs"
            variant="tertiary"
            onClick={() => {
              editor.cancel.reset();
              editor.refresh();
            }}
          >
            Refresh
          </Button>
        </Stack>
      )}
      <EditorComposer
        composer={composer}
        documents={editor.documents}
        remove={editor.remove}
        upload={upload}
        editable={workspace.editable}
        disabled={!canSend}
        blockedReason={blockedReason}
        open={editor.open}
        busy={busy}
        onSubmit={submit}
        queued={outgoing.queued}
        onQueueSendNow={sendNow}
        onQueueEdit={outgoing.amend}
        onQueueRemove={outgoing.remove}
      />
    </section>
  );
};
